import { history } from "./history";
import { store } from "./store";
import { userConstants } from "../constants/user.constants";
import { profileConstants } from "../constants/profile.constants";
import { disconnectAndReset } from "../services/socket";
import { invalidateRequestCache } from "./requestCache";
import { clearMediaPreloadCache } from "./mediaPreloader";

/**
 * Clears session (localStorage + Redux). Keeps Redux and storage in sync.
 * Also disconnects socket so no background polling continues (e.g. on session expiry).
 */
function clearSession() {
    disconnectAndReset();
    invalidateRequestCache();
    clearMediaPreloadCache();
    localStorage.removeItem('user');
    localStorage.removeItem('adMessageDisplayed');
    store.dispatch({ type: userConstants.LOGOUT });
    store.dispatch({ type: profileConstants.CLEAR_PROFILE });
}

/** Guard so multiple simultaneous 401s only trigger one redirect. */
let isRedirectingToLogin = false;

/**
 * Handles session expiry (401): clears session and redirects to login once.
 * If already on login page, only clears session (no redirect, no toast state) to avoid
 * continuous "Session expired" toasts and repeated failing requests.
 */
export function handleSessionExpired() {
    const isAlreadyOnLogin =
        typeof history?.location?.pathname === 'string' &&
        history.location.pathname.replace(/\/$/, '') === '/login';

    clearSession();

    if (isAlreadyOnLogin) {
        return;
    }

    if (!isRedirectingToLogin) {
        isRedirectingToLogin = true;
        history.push({ pathname: '/login', state: { sessionExpired: true } });
        setTimeout(() => { isRedirectingToLogin = false; }, 2000);
    }
}

/**
 * Parses NestJS class-validator 400 response format and returns a single display message.
 * Expects response.data.errors to be an object whose values may be arrays of
 * { property, constraints } (constraints = { constraintName: "message" }).
 * Returns null if the format is not present so callers can fall back to other message logic.
 *
 * @param {object} response - Axios error.response (has .data, .status)
 * @returns {string|null} User-facing validation message or null
 */
export function getValidationErrorMessage(response) {
    const errors = response?.data?.errors;
    if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
        return null;
    }
    const parts = [];
    for (const value of Object.values(errors)) {
        if (!Array.isArray(value)) continue;
        for (const item of value) {
            const constraints = item?.constraints;
            if (constraints && typeof constraints === 'object') {
                const messages = Object.values(constraints).filter((m) => typeof m === 'string');
                if (messages.length) parts.push(messages.join(' '));
            }
        }
    }
    return parts.length ? parts.join(' ') : null;
}

export function handleResponse(response) {
    // console.log('handleResponse :: ', response);
    if (response.text) //Handle fetch() errors
    {
        return response.text().then(text => {
            let data;
            try {
                data = text ? JSON.parse(text) : null;
            } catch (e) {
                return Promise.reject(new Error('Invalid JSON response'));
            }

            if (!response.ok) {
                const error = (data && data.message) || response.statusText;
                return Promise.reject(error);
            }

            return data;
        });
    }
    else if (!response) {
        clearSession();
        window.location.reload();
        return Promise.reject(new Error('No response from server'));
    } else { //Handle axios() errors
        if (response.status >= 200 && response.status < 300) {
            const { data = [] } = response;
            if (data && data.message) {
                return Promise.reject(new Error((data && data.message) || response.statusText));
            }
            return response.data;
        }
        const error = (response.data && response.data.errorMessage) || response.data?.message || response.statusText || 'Something went wrong';
        return Promise.reject(new Error(error));
    }
}

export function handleError(response) {
    if (!response) {
        return Promise.reject(new Error('Network error'));
    }
    if (response.status === 401) {
        handleSessionExpired();
    }
    let errorMessage;
    if (response.status === 400) {
        const validationMessage = getValidationErrorMessage(response);
        if (validationMessage) {
            errorMessage = validationMessage;
        }
    }
    if (!errorMessage) {
        errorMessage = (response.data && response.data.errorMessage) || response.data?.message || response.statusText || 'Something went wrong';
    }
    return Promise.reject(new Error(errorMessage));
}
